<template>
  <div class="sf-page">
    <top-header v-bind:title="`${$t('orders')} – ${supplier.supplierGroup}`">
      <template slot="right-button">
        <v-btn class="noprint"
               color="primary"
               outlined
               v-on:click="print">
          <v-icon left>ti-printer</v-icon>
          {{ $t('print') }}
        </v-btn>
        <v-btn class="noprint"
               color="primary"
               depressed
               v-bind:loading="exportLoading"
               v-on:click="exportCsv">
          <v-icon left>ti-file-spreadsheet</v-icon>
          {{ $t('export') }}
        </v-btn>
      </template>
    </top-header>
    <div class="sf-container sf-stack">
      <v-card class="sf-card noprint"
              flat>
        <div class="sf-card__header">
          <h2 class="sf-card__title">{{ $t('openCustomer') }}</h2>
        </div>
        <v-card-text class="sf-card__body">
          <p>{{ $t('infoOpenCustomer') }}</p>
          <p><strong>{{ $t('example') }}</strong>: 1234-1234-<span class="accent--text font-weight-bold text-decoration-underline">1234-1234</span></p>
          <v-row align="center">
            <v-col cols="12"
                   sm="">
              <v-text-field v-model="shortCode"
                            outlined
                            v-mask="'NNNN-NNNN'"
                            prepend-inner-icon="ti-user"
                            v-bind:label="$t('shortCode')"
                            hide-details
                            v-on:keyup="uppercase"
                            v-on:keyup.enter="openCustomer" />
            </v-col>
            <v-col cols="12"
                   sm="auto">
              <v-btn v-on:click="openCustomer"
                     color="accent"
                     depressed
                     large
                     v-bind:block="$vuetify.breakpoint.xsOnly">
                {{ $t('open') }}
              </v-btn>
            </v-col>
          </v-row>

          <v-alert class="mt-4 mb-0"
                   text
                   v-if="error"
                   type="error">
            {{ $t('error') }}
          </v-alert>
        </v-card-text>
      </v-card>

      <div class="sf-actions noprint"
           v-if="allowCustomer">
        <v-checkbox color="primary"
                    class="my-0"
                    hide-details="auto"
                    v-bind:label="$t('showCustomers')"
                    v-model="showCustomers" />
      </div>

      <order-summary-card hide-conditions
                          hide-edit
                          v-bind:show-customers="showCustomers"
                          v-bind:items-per-page="itemsPerPage"
                          v-bind:orders="showCustomers ? orderListCustomer : orderList"
                          v-bind:loading="loading"
                          v-bind:is-live="isLive" />
      <p class="sf-note">* {{ $t('allPricesCorrespondToTheRespectiveCustomerSpecificPriceListErrorsAndOmissionsExcepted') }}</p>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'

  import TopHeader from '@/components/common/TopHeader'
  import OrderSummaryCard from '@/components/OrderSummaryCard'

  export default {
    components: {
      OrderSummaryCard,
      TopHeader
    },
    computed: {
      allowCustomer() {
        if (this.orderListCustomer == null || this.orderListCustomer.length === 0) {
          return false
        }

        return this.orderListCustomer[0].customerId !== null && this.orderListCustomer[0].customerId.length > 0
      },
      orderList() {
        return this.orderListCustomer.reduce((result, current) => {
          const index = result.findIndex(p => p.productId === current.productId && p.readableDeliveryPeriod === current.readableDeliveryPeriod)

          if (index !== -1) {
            result[index].quantity += current.quantity
          } else {
            result.push({ ...current })
          }

          return result
        }, [])
      },
      orderListCustomer() {
        return [...this.orders].map(o => ({
          readableDeliveryPeriod: o.readableDeliveryPeriod,
          productTitle: o.productTitle,
          supplierFriendlyName: o.supplierFriendlyName,
          productId: o.productId,
          quantity: o.quantity,
          packaging: o.packaging,
          customerId: o.customerId,
          customerDescription: o.customerDescription
        }))
      },
      ...mapGetters([
        'orders',
        'priceForProduct',
        'isLive'
      ])
    },
    async created() {
      const invitationCode = this.$route.params.invitationCode

      if (invitationCode == null || invitationCode.length == 0) {
        return
      }

      this.invitationCode = invitationCode

      this.loading = true
      await this.$store.dispatch('loadSupplierByInvitationCode', { invitationCode })
      await this.$store.dispatch('loadOrdersForSupplierInvitation', { invitationCode })
      this.loading = false

      const supplier = this.$store.getters.supplierByInvitationCode(invitationCode)

      if (supplier == null) {
        this.$router.push({ name: 'home' })
      }

      if (supplier.state !== 'accepted') {
        this.$router.push({ name: 'registration-supplier', params: { invitationCode } })
      }

      this.supplier = Object.assign({}, supplier)
    },
    data: () => ({
      supplier: null,
      error: false,
      shortCode: null,
      itemsPerPage: 15,
      loading: false,
      exportLoading: false,
      invitationCode: null,
      showCustomers: false
    }),
    methods: {
      uppercase() {
        if (this.shortCode == null) {
          return
        }

        this.shortCode = this.shortCode.toUpperCase()
      },
      async openCustomer() {
        this.error = false

        this.loading = true
        const customer = await this.$store.dispatch('loadCustomerByShortCode', { shortCode: this.shortCode })
        this.loading = false


        if (customer == null || customer.status === 404) {
          this.error = true
          return
        }

        this.$router.push({ name: 'internal-supplier', params: { invitationCode: this.invitationCode, shortCode: this.shortCode } })
      },

      async exportCsv() {
        this.exportLoading = true

        const csv = await this.$store.dispatch('exportOrdersForSupplierInvitation', { invitationCode: this.invitationCode })

        let element = document.createElement('a')
        element.setAttribute('href', 'data:text/csv;charset=utf-8,%EF%BB%BF' + encodeURI(csv))
        element.setAttribute('download', 'export.csv')
        element.style.display = 'none'

        document.body.appendChild(element)

        element.click();

        document.body.removeChild(element)

        this.exportLoading = false
      },
      print() {
        this.itemsPerPage = -1

        setTimeout(() => {
          window.print()
        }, 1000)

      }
    },
    name: 'SupplierOrders'
  }
</script>

<i18n>
  {
    "de": {
      "allPricesCorrespondToTheRespectiveCustomerSpecificPriceListErrorsAndOmissionsExcepted": "Alle Preise entsprechen der kundenindividuellen Preisliste ohne Konditionen. Irrtümer und Änderungen vorbehalten.",
      "export": "Exportieren",
      "orders": "Bestellungen",
      "print": "Drucken",
      "shortCode": "Letzte 8 Zeichen des Kunden-Einladungscodes",
      "open": "Öffnen",
      "error": "Der eingegebene Code ist ungültig oder der Kunde hat sich noch nicht vorort angemeldet.",
      "openCustomer": "Kundenordersatz öffnen",
      "infoOpenCustomer": "Bitte geben Sie die letzten 8 Zeichen des Kunden-Einladungscodes. Diese finden Sie in der Einladungs-E-Mail des Kunden.",
      "example": "Beispiel",
      "showCustomers": "Kunden anzeigen"
    },
    "en": {
      "allPricesCorrespondToTheRespectiveCustomerSpecificPriceListErrorsAndOmissionsExcepted": "All prices correspond to the customer-specific price list. Errors and omissions excepted.",
      "export": "Export",
      "orders": "Orders",
      "print": "Print",
      "shortCode": "Short code",
      "open": "Open",
      "error": "The entered code is invalid or the customer has not yet registered on site.",
      "openCustomer": "Open customer order",
      "infoOpenCustomer": "Please enter the last 8 characters of the customer invitation code. You will find this in the customer's invitation email.",
      "example": "Example",
      "showCustomers": "Show customers"
    }
  }
</i18n>